import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client.js';

const KEY = (pid: string) => ['projects', pid, 'reifegrad'] as const;

export function useReifegrad(projectId: string | undefined) {
  return useQuery({
    queryKey: KEY(projectId ?? ''),
    queryFn: () => apiClient.get(`/api/v1/projects/${projectId}/reifegrad`),
    enabled: !!projectId,
  });
}

export function useUpsertReifegrad(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ schritt, data }: { schritt: number; data: Record<string, unknown> }) =>
      apiClient.put(`/api/v1/projects/${pid}/reifegrad/${schritt}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(pid) });
      // Dashboard liest schritte[] separat — Sidebar-Ampel + Step-Navigator
      // hängen daran. Ohne Invalidate würden die alten Werte gecached bleiben.
      qc.invalidateQueries({ queryKey: ['projects', pid, 'dashboard'] });
    },
  });
}

/**
 * Berechnet einen Reifegrad-Level (0-5) basierend auf Form-Vollständigkeit.
 *
 * - 0     → kein einziges Feld ausgefüllt (idle/grau)
 * - 1     → Pflichtfelder noch unvollständig (open/rot)
 * - 3     → Pflicht erfüllt, optionale Felder noch offen (wip/gelb)
 * - 4-5   → fast alle / alle Felder ausgefüllt (done/grün)
 */
export function computeMaturityFromForm(filled: boolean[], requiredMask: boolean[]): number {
  const total = filled.length;
  if (total === 0) return 0;
  const filledCount = filled.filter(Boolean).length;
  if (filledCount === 0) return 0;
  const requiredOk = requiredMask.every((req, i) => !req || filled[i]);
  if (!requiredOk) return 1;
  if (filledCount === total) return 5;
  const ratio = filledCount / total;
  if (ratio >= 0.8) return 4;
  return 3;
}

/**
 * Berechnet Reifegrad basierend auf Item-Count (fuer List-Tabs wie Team,
 * Anforderungen, Risiken, Meetings, ...).
 *
 * - 0 Items     → 0 (noch nichts erfasst)
 * - 1-2 Items   → 3 (in Arbeit)
 * - 3-4 Items   → 4 (substantiell)
 * - 5+ Items    → 5 (umfangreich)
 */
export function computeMaturityFromCount(count: number): number {
  if (count <= 0) return 0;
  if (count <= 2) return 3;
  if (count <= 4) return 4;
  return 5;
}

/**
 * Hook fuer List-Tabs: feuert PUT auf reifegrad/:schritt, sobald sich der
 * berechnete Level seit dem letzten Mount aendert. Best-Effort, kein
 * UI-Feedback. lastSync verhindert wiederholte identische PUTs.
 *
 * Initial-PUT pro Mount: ja (wenn count > 0). Server-Wert wird nicht mehr
 * abgefragt — Idempotenz wird vom pm-api gewaehrleistet (UPSERT).
 */
export function useSyncMaturityFromCount(
  projectId: string,
  schritt: number,
  count: number,
): void {
  const upsert = useUpsertReifegrad(projectId);
  const lastSync = useRef<number | null>(null);
  useEffect(() => {
    if (count < 0) return; // Sentinel: caller signalisiert "noch nicht geladen"
    const computed = computeMaturityFromCount(count);
    if (computed !== lastSync.current) {
      lastSync.current = computed;
      upsert.mutate({ schritt, data: { reifegrad: computed } });
    }
  }, [count, schritt, upsert]);
}
